import { createMemo, type Accessor } from 'solid-js';
import { createResponseErrorLogger } from '@trimble/logging';
import {
  createMapsServiceClient,
  MapsApiLayerStore,
} from '@data-access/geospatial/maps-service';
import {
  connectRegionToGeovectorRegion,
  isGeovectorServiceRegion,
  type GeovectorClientEnvironment,
  type GeovectorRegion,
} from '@trimble/utilities';
import {
  createFeaturesServiceClient,
  FeaturesApiService,
  featuresTilesApiVersion,
  FeaturesServiceFeatureStore,
} from '@data-access/geospatial/features-service';
import {
  type GeospatialLogger,
  type IFeatureService,
  type ILayerService,
  type SystemLayerConfig,
} from '@trimble/geospatial';
import { isSystemLayerId } from './system-layers';

export interface FeatureExtractionRepositories {
  readonly layerRepository: ILayerService;
  readonly featureRepository: IFeatureService;
  readonly featuresApiService: FeaturesApiService;
}

export interface CreateFeatureExtractionRepositoriesParams {
  readonly envName: Accessor<string>;
  readonly region: Accessor<GeovectorRegion | undefined>;
  readonly logger: GeospatialLogger;
  getAccessToken(): Promise<string>;
  /** Sync token for FeaturesApiService; its per-request auth overrides getAccessToken, so calls made while empty are unauthenticated. */
  readonly authToken: Accessor<string | undefined>;
  readonly fxMapsServiceBaseUrl?: Accessor<string | undefined>;
  readonly fxFeaturesServiceBaseUrl?: Accessor<string | undefined>;
}

// Feature-extraction backends have no dev deployment.
const PROD_MAPS_ENV_NAMES: ReadonlySet<string> = new Set([
  'prod',
  'rc',
  'localprod',
]);

function mapEnvName(envName: string): GeovectorClientEnvironment {
  return PROD_MAPS_ENV_NAMES.has(envName) ? 'prod' : 'stage';
}

export function resolveFxServiceBaseUrl(
  template: string | undefined,
  region: GeovectorRegion
): string | undefined {
  return template?.replace('{region}', region);
}

export function resolveFxFeaturesServiceBaseUrl(
  template: string | undefined,
  region: GeovectorRegion,
  envName: string
): string | undefined {
  return resolveFxServiceBaseUrl(template, region)?.replace(
    '{version}',
    featuresTilesApiVersion(mapEnvName(envName), region)
  );
}

export function resolveFxTilesServiceBaseUrl(
  template: string | undefined,
  region: GeovectorRegion,
  envName: string
): string {
  const env = mapEnvName(envName);
  const version = featuresTilesApiVersion(env, region);
  const configuredUrl = resolveFxFeaturesServiceBaseUrl(
    template,
    region,
    envName
  );
  if (configuredUrl !== undefined) {
    return configuredUrl;
  }
  const host =
    env === 'prod'
      ? 'cloud.api.trimble.com'
      : 'cloud.stage.api.trimblecloud.com';
  return `https://${host}/features-service/features-${region}/${version}`;
}

export function createMapsRegion(
  connectLocation: Accessor<string | undefined>
): Accessor<GeovectorRegion | undefined> {
  const mapsRegion = createMemo<GeovectorRegion | undefined>(() => {
    const location = connectLocation();
    const geovectorRegion = location
      ? connectRegionToGeovectorRegion(location)
      : undefined;
    return isGeovectorServiceRegion(geovectorRegion)
      ? geovectorRegion
      : undefined;
  });
  return mapsRegion;
}

export function createFeatureExtractionRepositories(
  params: CreateFeatureExtractionRepositoriesParams
): Accessor<FeatureExtractionRepositories | undefined> {
  const {
    envName,
    region,
    logger,
    getAccessToken,
    authToken,
    fxMapsServiceBaseUrl,
    fxFeaturesServiceBaseUrl,
  } = params;
  const repositories = createMemo<FeatureExtractionRepositories | undefined>(
    () => {
      const mapsRegion = region();
      if (mapsRegion === undefined) {
        return undefined;
      }

      const env = mapEnvName(envName());
      const apiErrorLogger = createResponseErrorLogger(
        logger.createNamedLogger('ApiError')
      );
      const mapsBaseUrl = resolveFxServiceBaseUrl(
        fxMapsServiceBaseUrl?.(),
        mapsRegion
      );
      const featuresBaseUrl = resolveFxFeaturesServiceBaseUrl(
        fxFeaturesServiceBaseUrl?.(),
        mapsRegion,
        envName()
      );

      const mapsClient = createMapsServiceClient({
        env,
        region: mapsRegion,
        ...(mapsBaseUrl !== undefined ? { baseUrl: mapsBaseUrl } : {}),
      });
      mapsClient.setConfig({ auth: getAccessToken });
      mapsClient.interceptors.response.use(apiErrorLogger);

      const featuresClient = createFeaturesServiceClient({
        env,
        region: mapsRegion,
        ...(featuresBaseUrl !== undefined ? { baseUrl: featuresBaseUrl } : {}),
      });
      featuresClient.setConfig({ auth: getAccessToken });
      featuresClient.interceptors.response.use(apiErrorLogger);

      const systemLayerConfig: SystemLayerConfig = { isSystemLayerId };

      return {
        layerRepository: new MapsApiLayerStore(
          mapsClient,
          logger,
          systemLayerConfig
        ),
        featureRepository: new FeaturesServiceFeatureStore(
          featuresClient,
          logger
        ),
        featuresApiService: new FeaturesApiService({
          client: () => featuresClient,
          authToken: () => authToken() ?? '',
          queryScope: () => `${env}:${mapsRegion}`,
        }),
      };
    }
  );
  return repositories;
}
